import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { barsApi } from '@/api/endpoints/bars';
import { barMembersApi } from '@/api/endpoints/bar-members';
import { EventCard } from '@/components/domain';
import { SEOHead } from '@/components/seo';
import { PageSpinner, Badge } from '@/components/ui';
import { Tv, Wine, Sun, Dog } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFilters } from '@/contexts/FilterContext';
import { analytics } from '@/lib/analytics';
import toast from 'react-hot-toast';
import { isActiveOnDate } from '@/lib/rrule';
import { getTimeForSort } from '@/features/events/utils/eventSortUtils';
import { useDisintegrate } from '@/hooks/useDisintegrate';
import { generateBarSchema, generateBreadcrumbSchema } from '@/lib/structured-data';
import {
  ParallaxHero,
  BarNameRow,
  ManageBarButton,
  CompactContactSection,
  HoursExpander,
  SpecialsSection,
  HappyHoursSection,
  ClaimBarSection,
} from '../components';

export function BarDetailPage() {
  const { id: idOrSlug } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isAdmin, managedBars } = useAuth();
  const { date } = useFilters();

  const { containerRef, disintegrate } = useDisintegrate({
    onComplete: () => navigate(-1),
    duration: 2000,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['bar', idOrSlug],
    queryFn: () => barsApi.getById(idOrSlug!),
    enabled: !!idOrSlug,
  });

  // Use the actual bar ID for mutations (not the slug)
  const barId = data?.bar?.id;

  // Track bar view in analytics
  useEffect(() => {
    if (data?.bar) {
      analytics.viewBar(data.bar.id, data.bar.name);
    }
  }, [data?.bar?.id, data?.bar?.name]);

  const claimMutation = useMutation({
    mutationFn: () => barMembersApi.claim(barId!),
    onSuccess: (result) => {
      if (result.autoApproved) {
        toast.success('Bar claimed successfully!');
        queryClient.invalidateQueries({ queryKey: ['me'] });
      } else {
        toast.success('Claim request submitted! An admin will review it.');
      }
      queryClient.invalidateQueries({ queryKey: ['bar', idOrSlug] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to claim bar');
    },
  });

  if (isLoading) return <PageSpinner />;

  if (isError || !data?.bar) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--color-muted)]">Bar not found</p>
        <Link to="/bars" className="mt-4 inline-block text-accent-primary">
          Back to bars
        </Link>
      </div>
    );
  }

  const { bar } = data;

  // Check if user already manages this bar
  const userManagesBar = managedBars.some((mb) => mb.barId === bar.id);

  // Filter events by selected date and sort by time
  const filteredEvents = (bar.events ?? [])
    .filter((event) => {
      // If event has rrule, use rrule logic
      if (event.rrule) {
        return isActiveOnDate(event.rrule, date);
      }
      // For one-time events, check if startDatetime matches the selected date
      if (event.startDatetime) {
        const eventDate = event.startDatetime.split('T')[0];
        return eventDate === date;
      }
      // No rrule and no startDatetime - don't show
      return false;
    })
    .sort((a, b) => {
      const aTime = getTimeForSort(a.startDatetime);
      const bTime = getTimeForSort(b.startDatetime);
      return aTime.localeCompare(bTime);
    });

  // Extract city from address for SEO
  const cityMatch = bar.address?.match(/,\s*([^,]+),\s*[A-Z]{2}/);
  const city = cityMatch ? cityMatch[1].trim() : '';
  const specialsCount = bar.specials?.length || 0;
  const eventsCount = filteredEvents.length;

  // Use slug for canonical URL, fall back to ID
  const canonicalPath = `/bars/${bar.slug || bar.id}`;

  // Generate structured data for SEO
  const barSchema = generateBarSchema(bar);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Bars', path: '/bars' },
    { name: bar.name, path: canonicalPath },
  ]);

  return (
    <>
      <SEOHead
        title={`${bar.name} - Specials & Events`}
        description={`${bar.name}${city ? ` in ${city}` : ''}: ${specialsCount} specials, ${eventsCount} events. ${bar.description?.slice(0, 100) || 'View hours, specials, and events.'}`}
        canonicalPath={canonicalPath}
        ogImage={bar.logoUrl || undefined}
        ogType="place"
        structuredData={[barSchema, breadcrumbSchema]}
      />
      <div ref={containerRef} className="space-y-6 md:space-y-8">
      {/* Parallax Hero Image (includes back button) */}
      <ParallaxHero imageUrl={bar.logoUrl} barName={bar.name} />

      {/* Bar Name + Favorite */}
      <BarNameRow barId={bar.id} name={bar.name} onDisintegrate={disintegrate} />

      {/* Description */}
      {bar.description && (
        <p className="text-[var(--color-muted)]">{bar.description}</p>
      )}

      {/* Badges */}
      <div className="flex flex-wrap gap-1">
        {bar.isClaimed && <Badge variant="success">Claimed</Badge>}
        {bar.goodForWatchingSports && <Badge variant="secondary"><Tv className="h-3 w-3" /> Sports</Badge>}
        {bar.servesBrunch && <Badge variant="primary"><Wine className="h-3 w-3" /> Brunch</Badge>}
        {bar.outdoorSeating && <Badge><Sun className="h-3 w-3" /> Patio</Badge>}
        {bar.allowsDogs && <Badge><Dog className="h-3 w-3" /> Dog Friendly</Badge>}
      </div>

      {/* Manage Bar Button (for owners) */}
      {(userManagesBar || isAdmin) && <ManageBarButton barId={bar.id} />}

      {/* Contact Section */}
      <CompactContactSection
        address={bar.address}
        phoneNumber={bar.phoneNumber}
        websiteUrl={bar.websiteUrl}
      />

      {/* Hours */}
      {bar.hours && Object.keys(bar.hours).length > 0 && (
        <HoursExpander hours={bar.hours} />
      )}

      {/* Events (first) */}
      {filteredEvents.length > 0 && (
        <div>
          <h2 className="mb-4 text-lg font-semibold">Events</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {filteredEvents.map((event) => (
              <EventCard key={event.id} event={event} showBar={false} compact={false} />
            ))}
          </div>
        </div>
      )}

      {/* Specials (excludes happy hour, has food/drink toggle) */}
      {bar.specials && bar.specials.length > 0 && (
        <SpecialsSection specials={bar.specials} selectedDate={date} />
      )}

      {/* Happy Hours (aggregated by time window) */}
      {bar.specials && bar.specials.length > 0 && (
        <HappyHoursSection specials={bar.specials} selectedDate={date} />
      )}

      {/* Claim Bar Section (at bottom) */}
      <ClaimBarSection
        barName={bar.name}
        isClaimed={bar.isClaimed}
        userManagesBar={userManagesBar}
        isAuthenticated={isAuthenticated}
        onClaim={() => claimMutation.mutate()}
        isPending={claimMutation.isPending}
      />
      </div>
    </>
  );
}
